/**
 * `GET /api/health`.
 */
import type { HealthResponse } from '@forsys/shared';
import { ApiError, apiFetch } from './client';

/**
 * Throws unless the answer is really ours.
 *
 * "Something answered 200" is not "the backend is up". In development `/api` is
 * a proxy to a port, and whatever else is listening on that port answers too —
 * another project's dev server will happily return its own `index.html`, or its
 * own JSON. Checking the shape is what lets the health banner say "this is not
 * the backend" instead of showing a green light over every request failing.
 */
export async function getHealth(): Promise<HealthResponse> {
  const body = await apiFetch<unknown>('/health');
  if (!isHealthResponse(body)) {
    throw new ApiError(
      200,
      'unexpected_response',
      'GET /api/health answered, but not with a FORSYS health payload.',
    );
  }
  return body;
}

function isHealthResponse(body: unknown): body is HealthResponse {
  if (typeof body !== 'object' || body === null) return false;
  const candidate = body as Partial<HealthResponse>;
  return (
    candidate.status === 'ok' &&
    typeof candidate.version === 'string' &&
    typeof candidate.release === 'object' &&
    candidate.release !== null &&
    typeof candidate.schema?.state === 'string' &&
    typeof candidate.database?.state === 'string'
  );
}
